import { yupResolver } from '@hookform/resolvers/yup';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as yup from 'yup';
import * as AdminViewModels from '../../../../app/viewmodels/AdminViewModels';
import { IModalContext, ModalChildComponentContextKey } from '../../../models';
import { useDeleteKeyFactsMutation } from '../../../queries/KeyFacts/useDeleteKeyFacts';
import { Button } from '../../../web/components-v2/Button';
import { Pill } from '../../../web/components-v2/Pill';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../web/components/ConfirmationDialog';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { asModalComponent } from '../../../web/components/Modal';
import { TagPickerPopover } from '../../../web/components/reporting/emails/TagPickerPopover';
import { WarningIcon } from '../../../web/components/svgs/icons/WarningIcon';
import { styleSheet } from './styles';

const DeleteKeyFactsConfirmationOptions: IConfirmationDialogOption[] = [
	{
		isDestructive: true,
		title: 'Delete',
	},
	{
		isCancel: true,
		title: 'Cancel',
	},
];

interface IProps extends IModalContext {
	userSession: AdminViewModels.AdminUserSessionContext;
}

const _DeleteKeyFactsModal = ({ parentModal: { onRequestClose } }: IProps) => {
	const [showConfirmation, setShowConfirmation] = React.useState<boolean>(false);
	const [selectedTags, setSelectedTags] = React.useState<Api.IAccountTag[]>([]);

	const handleCancel = () => {
		onRequestClose();
	};

	const accountDetailsContext = React.useContext(AdminViewModels.AccountDetailsContext);

	const resourceSelectorSettings = accountDetailsContext.account?.preferences?.resourceSelectorSettings;
	const getCustomTitle = (kind: Api.KeyDateKind) =>
		resourceSelectorSettings?.[kind as unknown as Api.ResourceSelectorId]?.displayName || kind;

	const keyDateKinds = [
		{ value: Api.KeyDateKind.Birthday, label: 'Birthday' },
		{ value: Api.KeyDateKind.Renewal, label: 'Renewal' },
		{ value: Api.KeyDateKind.Anniversary, label: 'Anniversary' },
		{ value: Api.KeyDateKind.Wedding, label: 'Wedding' },
		{ value: Api.KeyDateKind.FinancialReview, label: 'Client Review' },
		{ value: Api.KeyDateKind.Custom1, label: getCustomTitle(Api.KeyDateKind.Custom1) },
		{ value: Api.KeyDateKind.Custom2, label: getCustomTitle(Api.KeyDateKind.Custom2) },
		{ value: Api.KeyDateKind.Custom3, label: getCustomTitle(Api.KeyDateKind.Custom3) },
		{ value: Api.KeyDateKind.Custom4, label: getCustomTitle(Api.KeyDateKind.Custom4) },
		{ value: Api.KeyDateKind.Custom5, label: getCustomTitle(Api.KeyDateKind.Custom5) },
		{ value: Api.KeyDateKind.Custom6, label: getCustomTitle(Api.KeyDateKind.Custom6) },
		{ value: Api.KeyDateKind.Custom7, label: getCustomTitle(Api.KeyDateKind.Custom7) },
		{ value: Api.KeyDateKind.Custom8, label: getCustomTitle(Api.KeyDateKind.Custom8) },
		{ value: Api.KeyDateKind.Custom9, label: getCustomTitle(Api.KeyDateKind.Custom9) },
		{ value: Api.KeyDateKind.Custom10, label: getCustomTitle(Api.KeyDateKind.Custom10) },
	];

	const impersonationContext = { account: accountDetailsContext.account };
	const deleteKeyFactsMutation = useDeleteKeyFactsMutation({
		onSuccess: modifiedContactCount => {
			onRequestClose();
			if (modifiedContactCount > 0) {
				toast.success(
					`Key facts deleted from ${modifiedContactCount} ${modifiedContactCount === 1 ? 'contact' : 'contacts'}`
				);
			} else {
				// A zero count is the only signal that the chosen scope matched nothing (e.g. a tag no contact carries).
				toast.warning('No contacts matched — no key facts were deleted');
			}
		},
		impersonationContext,
	});

	const validationSchema = yup.object({
		keyDateKind: yup.string().required('Key date kind is required'),
	});

	const methods = useForm<{
		keyDateKind: string | undefined;
	}>({
		defaultValues: {
			keyDateKind: undefined,
		},
		resolver: yupResolver(validationSchema),
		mode: 'onChange',
	});
	const formValues = methods.watch();

	const selectedTitle = keyDateKinds.find(x => x.value === formValues.keyDateKind)?.label;
	const tagNames = selectedTags.map(tag => tag.name).filter((name): name is string => !!name);

	const handleTagSelected = (tag: Api.IAccountTag) => {
		if (!tag.name) {
			return;
		}
		setSelectedTags(current => (current.some(x => x.name === tag.name) ? current : [...current, tag]));
	};

	const handleTagRemoved = (name: string) => {
		setSelectedTags(current => current.filter(tag => tag.name !== name));
	};

	const handleDeleteClick = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setShowConfirmation(true);
	};

	const handleConfirmationClose = (result: IConfirmationDialogOption<boolean>) => {
		if (result?.isDestructive) {
			deleteKeyFactsMutation
				.mutateAsync({
					keyFactKind: formValues.keyDateKind as Api.KeyDateKind,
					tags: tagNames,
				})
				.catch(() => {
					toast.error('Failed to delete key facts');
				});
		}
		setShowConfirmation(false);
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={handleDeleteClick}>
					<div className={css(styleSheet.wrapper)}>
						<h3 className={css(styleSheet.heading)}>Delete Key Facts</h3>
						<p className={css(styleSheet.text)}>Select the kind of key fact to delete:</p>
						{deleteKeyFactsMutation.isPending ? (
							<LoadingSpinner type='small' />
						) : (
							<>
								<Controller
									name='keyDateKind'
									control={methods.control}
									render={({ field: { ref, ...fieldProps } }) => (
										<Select value={fieldProps.value ?? ''} onValueChange={fieldProps.onChange}>
											<SelectTrigger styles={styleSheet.selectTrigger}>
												<SelectValue placeholder='Select the Kind of Key Date' />
											</SelectTrigger>
											<SelectContent>
												{keyDateKinds.map(option => (
													<SelectItem key={option.value} value={option.value}>
														{option.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									)}
								/>
								<p className={css(styleSheet.text)}>
									Optionally limit the deletion to contacts with any of these tags. Leave empty to include every contact
									in the account.
								</p>
								<div className={css(styleSheet.tagRow)}>
									{selectedTags.map(tag => (
										<Pill
											key={tag.name}
											variant='tag'
											color={tag.color || 'blue'}
											label={tag.name || ''}
											onRemove={handleTagRemoved}
										/>
									))}
									<TagPickerPopover
										impersonationContext={impersonationContext}
										onTagSelected={handleTagSelected}
										trigger={
											<Button variant='secondary' type='button'>
												Add tag
											</Button>
										}
									/>
								</div>
								<footer className={css(styleSheet.footer)}>
									<Button
										variant='destructive'
										styles={[styleSheet.continueButton]}
										disabled={!methods.formState.isValid || methods.formState.isSubmitting}
										type='submit'
									>
										Delete
									</Button>
									<Button variant='secondary' onClick={handleCancel} type='button'>
										Cancel
									</Button>
								</footer>
							</>
						)}
					</div>
				</form>
			</FormProvider>
			<ConfirmationDialog
				className={css(styleSheet.confirmationDialog)}
				icon={<WarningIcon />}
				modalProps={{
					isOpen: showConfirmation,
					onRequestClose: handleConfirmationClose,
				}}
				options={DeleteKeyFactsConfirmationOptions}
				title={
					tagNames.length
						? `Are you sure you want to delete all ${selectedTitle} key facts from contacts tagged ${tagNames.join(
								', '
							)}? This operation cannot be undone.`
						: `Are you sure you want to delete all ${selectedTitle} key facts for this account? This operation cannot be undone.`
				}
			/>
		</>
	);
};

const DeleteKeyFactsModalAsObserver = observer(_DeleteKeyFactsModal);
const DeleteKeyFactsModalWithContext = inject(ModalChildComponentContextKey)(DeleteKeyFactsModalAsObserver);
export const DeleteKeyFactsModal = asModalComponent(DeleteKeyFactsModalWithContext, {
	className: 'schedule-meeting--modal',
	useDefaultHeader: true,
});
